import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { MediaUploader } from '@/components/MediaUploader';
import { Trash2, GripVertical, Image as ImageIcon, Video } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ScreensaverMedia {
  id: string;
  municipality_id: string;
  image_url: string;
  display_order: number;
  media_type: 'image' | 'video';
}

interface ScreensaverManagerProps {
  municipalityId: string;
  municipalityName: string;
}

const SortableMediaItem = ({ media, onDelete }: { 
  media: ScreensaverMedia; 
  onDelete: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-white rounded-lg border shadow-sm"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
      </div>
      
      {media.media_type === 'video' ? (
        <div className="relative w-14 h-14 md:w-20 md:h-20 rounded overflow-hidden bg-gray-900 flex items-center justify-center flex-shrink-0">
          <video
            src={media.image_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Video className="w-4 h-4 md:w-6 md:h-6 text-white" />
          </div>
        </div>
      ) : (
        <img
          src={media.image_url}
          alt="Media salvapantallas"
          className="w-14 h-14 md:w-20 md:h-20 object-cover rounded flex-shrink-0"
        />
      )}
      
      <div className="flex-1 flex flex-col gap-0.5 md:gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`px-1.5 md:px-2 py-0.5 rounded text-xs font-medium ${
            media.media_type === 'video' 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {media.media_type === 'video' ? (
              <span className="flex items-center gap-1">
                <Video className="w-3 h-3" />
                <span className="hidden sm:inline">Video</span>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                <span className="hidden sm:inline">Imagen</span>
              </span>
            )}
          </span>
        </div>
        <span className="text-xs md:text-sm text-gray-600">Orden: {media.display_order + 1}</span>
      </div>
      
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="h-8 w-8 md:h-9 md:w-auto md:px-3 p-0 flex-shrink-0"
        onClick={() => onDelete(media.id)}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const ScreensaverManager = ({ municipalityId, municipalityName }: ScreensaverManagerProps) => {
  const [mediaItems, setMediaItems] = useState<ScreensaverMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [delay, setDelay] = useState(5000);
  const [screensaverEnabled, setScreensaverEnabled] = useState(true);
  const [activationTimeout, setActivationTimeout] = useState(30000);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadMedia();
    loadDelay();
  }, [municipalityId]);

  const [newMediaFile, setNewMediaFile] = useState<File | null>(null);
  const [newMediaPreview, setNewMediaPreview] = useState<string | null>(null);
  const [newMediaType, setNewMediaType] = useState<'image' | 'video' | null>(null);

  const loadDelay = async () => {
    try {
      const data = await api.municipalities.get(municipalityId);
      if (data?.screensaver_delay) setDelay(data.screensaver_delay);
      setScreensaverEnabled(data?.screensaver_enabled ?? true);
      setActivationTimeout(data?.screensaver_timeout ?? 30000);
    } catch (error) {
      console.error('Error loading screensaver delay:', error);
    }
  };

  const handleDelayChange = async (newDelay: number) => {
    setDelay(newDelay);
    try {
      await api.municipalities.update(municipalityId, { screensaver_delay: newDelay });
    } catch (error) {
      console.error('Error updating screensaver delay:', error);
      toast({ title: 'Error', description: 'No se pudo guardar el tiempo de transición', variant: 'destructive' });
    }
  };

  const handleEnabledChange = async (enabled: boolean) => {
    const previous = screensaverEnabled;
    setScreensaverEnabled(enabled);
    try {
      await api.municipalities.update(municipalityId, { screensaver_enabled: enabled });
      toast({
        title: enabled ? 'Salvapantallas activado' : 'Salvapantallas desactivado',
        description: 'El display aplicará el cambio automáticamente en unos segundos',
      });
    } catch (error) {
      setScreensaverEnabled(previous);
      console.error('Error updating screensaver state:', error);
      toast({ title: 'Error', description: 'No se pudo cambiar el estado del salvapantallas', variant: 'destructive' });
    }
  };

  const handleActivationTimeoutCommit = async (newTimeout: number) => {
    try {
      await api.municipalities.update(municipalityId, { screensaver_timeout: newTimeout });
      toast({
        title: 'Tiempo de activación actualizado',
        description: 'El display aplicará el cambio automáticamente en unos segundos',
      });
    } catch (error) {
      console.error('Error updating screensaver timeout:', error);
      toast({ title: 'Error', description: 'No se pudo guardar el tiempo de activación', variant: 'destructive' });
      loadDelay();
    }
  };

  const loadMedia = async () => {
    try {
      const data = await api.screensaverImages.list(municipalityId);
      setMediaItems((data || []).map((item: any) => ({ ...item, media_type: item.media_type || 'image' })));
    } catch (error) {
      console.error('Error loading screensaver media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMediaChange = (file: File | null, type: 'image' | 'video') => {
    setNewMediaFile(file);
    setNewMediaType(type);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePreview = () => {
    setNewMediaFile(null);
    setNewMediaPreview(null);
    setNewMediaType(null);
  };

  const handleMediaUpload = async () => {
    if (!newMediaFile || !newMediaType) return;

    try {
      const extension = newMediaFile.name.split('.').pop()?.toLowerCase() || '';
      const fileName = `screensaver/${municipalityId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${extension}`;

      const { url: publicUrl } = await api.storage.upload(newMediaFile, fileName, 'images');

      const maxOrder = mediaItems.length > 0
        ? Math.max(...mediaItems.map(item => item.display_order))
        : -1;

      await api.screensaverImages.create(municipalityId, {
        image_url: publicUrl,
        display_order: maxOrder + 1,
        media_type: newMediaType,
      });

      toast({
        title: newMediaType === 'video' ? 'Video agregado' : 'Imagen agregada',
        description: `${newMediaType === 'video' ? 'El video' : 'La imagen'} se agregó al salvapantallas exitosamente`,
      });

      handleRemovePreview();
      loadMedia();
    } catch (error) {
      console.error('Error adding media:', error);
      toast({
        title: "Error",
        description: "No se pudo agregar el archivo",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.screensaverImages.delete(municipalityId, id);
      toast({ title: 'Elemento eliminado', description: 'El archivo fue eliminado del salvapantallas' });
      loadMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast({ title: 'Error', description: 'No se pudo eliminar el archivo', variant: 'destructive' });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = mediaItems.findIndex(item => item.id === active.id);
      const newIndex = mediaItems.findIndex(item => item.id === over.id);

      const newMediaItems = arrayMove(mediaItems, oldIndex, newIndex);
      setMediaItems(newMediaItems);

      try {
        const updates = newMediaItems.map((item, index) => ({
          id: item.id,
          display_order: index
        }));

        for (const update of updates) {
          await api.screensaverImages.update(municipalityId, update.id, { display_order: update.display_order });
        }

        toast({
          title: "Orden actualizado",
          description: "El orden de los elementos se guardó correctamente"
        });
      } catch (error) {
        console.error('Error updating order:', error);
        toast({
          title: "Error",
          description: "No se pudo actualizar el orden",
          variant: "destructive"
        });
        loadMedia();
      }
    }
  };

  const imageCount = mediaItems.filter(m => m.media_type === 'image').length;
  const videoCount = mediaItems.filter(m => m.media_type === 'video').length;

  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <CardTitle className="text-lg md:text-xl">
          Salvapantallas - {municipalityName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 pt-0 md:pt-0">
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="screensaver-enabled" className="text-sm md:text-base">
                Activar salvapantallas
              </Label>
              <p className="text-xs text-muted-foreground">
                Puedes desactivarlo sin borrar las imágenes configuradas.
              </p>
            </div>
            <Switch
              id="screensaver-enabled"
              checked={screensaverEnabled}
              onCheckedChange={handleEnabledChange}
            />
          </div>

          <div className="space-y-2 md:space-y-3">
            <Label className="text-sm md:text-base">
              {screensaverEnabled ? 'Activar salvapantallas' : 'Volver al inicio'} tras {activationTimeout / 1000} segundos de inactividad
            </Label>
            <Slider
              value={[activationTimeout]}
              onValueChange={(value) => setActivationTimeout(value[0])}
              onValueCommit={(value) => handleActivationTimeoutCommit(value[0])}
              min={10000}
              max={600000}
              step={10000}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Ajustable entre 10 segundos y 10 minutos. Si el salvapantallas está desactivado, el display volverá al inicio.
            </p>
          </div>
        </div>

        <div className="space-y-2 md:space-y-3">
          <Label className="text-sm md:text-base">Tiempo entre imágenes: {delay / 1000} segundos</Label>
          <Slider
            value={[delay]}
            onValueChange={(value) => handleDelayChange(value[0])}
            min={3000}
            max={20000}
            step={1000}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            Ajusta el tiempo que se muestra cada imagen (3-20 segundos).
          </p>
        </div>

        {mediaItems.length < 5 ? (
          <div className="space-y-3">
            <MediaUploader
              label="Agregar imagen al salvapantallas"
              preview={newMediaPreview}
              mediaType={newMediaType}
              onMediaChange={handleMediaChange}
              onRemove={handleRemovePreview}
              recommendedResolution="4500×8000"
              maxImageSizeMB={30}
            />
            {newMediaFile && (
              <Button 
                onClick={handleMediaUpload}
                className="w-full"
              >
                Subir imagen
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            Has alcanzado el máximo de 5 imágenes en el salvapantallas.
          </p>
        )}

        {loading ? (
          <div>Cargando contenido...</div>
        ) : mediaItems.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No hay contenido configurado para el salvapantallas.
            Agrega imágenes arriba.
          </p>
        ) : (
          <div className="space-y-2 md:space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs md:text-sm text-gray-600">
                Arrastra los elementos para cambiar el orden:
              </p>
              <div className="flex items-center gap-2 md:gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {imageCount} {imageCount === 1 ? 'imagen' : 'imágenes'}
                </span>
                {videoCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Video className="w-3 h-3" />
                    {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                  </span>
                )}
              </div>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={mediaItems.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {mediaItems.map((media) => (
                  <SortableMediaItem
                    key={media.id}
                    media={media}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
